import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { auth } from '../config/firebase';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import toast from 'react-hot-toast';
import { User } from '@brovicms/models';

// Auth context type
interface AuthContextType {
  user: User | null;
  isAdmin: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      if (firebaseUser) {
        const userData: User = {
          id: firebaseUser.uid,
          email: firebaseUser.email || '',
          displayName: firebaseUser.displayName || undefined,
          photoURL: firebaseUser.photoURL || undefined,
          isEmailVerified: firebaseUser.emailVerified,
          createdAt: new Date(),
          updatedAt: new Date(),
          preferences: {
            language: 'en',
            theme: 'light',
            fontSize: 'medium',
            readingSpeed: 'normal',
            notifications: {
              email: true,
              push: true,
              whatsapp: false
            },
            privacy: {
              profileVisible: true,
              readingHistoryVisible: true
            }
          },
          library: {
            bookmarks: [],
            favorites: [],
            readingHistory: [],
            downloads: [],
            collections: []
          }
        };
        setUser(userData);
        // Check if user is admin
        const adminEmails = process.env.REACT_APP_ADMIN_EMAILS?.split(',');
        const isAdminEmail = firebaseUser.email && (
          firebaseUser.email.includes('admin') || 
          firebaseUser.email.includes('brovicms') || 
          adminEmails?.includes(firebaseUser.email)
        );
        setIsAdmin(!!isAdminEmail);
      } else {
        setUser(null);
        setIsAdmin(false);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = async (email: string, password: string): Promise<User> => {
    try {
      setLoading(true);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      
      // Check if user is admin
      const adminEmails = process.env.REACT_APP_ADMIN_EMAILS?.split(',') || [
        'admin@brovicms.com',
        'bharat.chavan@brovitech.com'
      ];
      const adminCheck = firebaseUser.email && (
        firebaseUser.email.includes('admin') || 
        firebaseUser.email.includes('brovicms') || 
        adminEmails.includes(firebaseUser.email)
      );
      if (!adminCheck) {
        await signOut(auth);
        throw new Error('Access denied. Admin privileges required.');
      }
      
      const userData: User = {
        id: firebaseUser.uid,
        email: firebaseUser.email || '',
        displayName: firebaseUser.displayName || undefined,
        photoURL: firebaseUser.photoURL || undefined,
        isEmailVerified: firebaseUser.emailVerified,
        createdAt: new Date(),
        updatedAt: new Date(),
        preferences: {
          language: 'en',
          theme: 'light',
          fontSize: 'medium',
          readingSpeed: 'normal',
          notifications: {
            email: true,
            push: true,
            whatsapp: false
          },
          privacy: {
            profileVisible: true,
            readingHistoryVisible: true
          }
        },
        library: {
          bookmarks: [],
          favorites: [],
          readingHistory: [],
          downloads: [],
          collections: []
        }
      };
      
      setIsAdmin(true);
      toast.success('Welcome back!');
      return userData;
    } catch (error: any) {
      console.error('Login error:', error);
      toast.error(error.message || 'Login failed');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await signOut(auth);
      setUser(null);
      setIsAdmin(false);
      toast.success('Logged out successfully');
    } catch (error: any) {
      console.error('Logout error:', error);
      toast.error('Logout failed');
    }
  };

  const value: AuthContextType = {
    user,
    isAdmin,
    loading,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
